/**
 * TriggernometryDash — Entry Point
 *
 * Initializes the game and manages the full UI lifecycle:
 * title screen → level select → play → results → back to menu.
 */

import { Game } from '@/game/game';
import type { LevelData } from '@/game/level';
import { StorageManager } from '@/storage/save-data';
import { ResultsScreen } from '@/ui/results-screen';
import { LevelSelectScreen, type LevelInfo } from '@/ui/level-select';
import { SettingsScreen } from '@/ui/settings-screen';
import { EditorScreen } from '@/editor/editor-screen';
import { cloneLevel } from '@/editor/editor-clone';
import { parseImport, planConflicts, applyConflictPlan } from '@/editor/editor-pack';
import { showAlert, showConfirm } from '@/editor/editor-dialogs';
import { Account } from '@/sync/account';
import { SyncManager } from '@/sync/sync-manager';
import { AccountScreen } from '@/ui/account-screen';
import { BrowseScreen } from '@/ui/browse-screen';
import { TitleBackground } from '@/ui/title-background';
import { MenuMusic } from '@/audio/menu-music';
import { AudioEngine } from '@/audio/audio-engine';
import { SFX } from '@/audio/sfx';
import { toast } from '@/ui/toast';
import { setOnAuthLost } from '@/sync/auth-boundary';
import {
  performSyncAction,
  pullRemoteOnlyLevel,
  remixLevel as remixLevelAction,
  updateDownloadedLevel,
  unlinkDownloadedLevel,
} from '@/sync/sync-actions';
import { submitCompletion, type CompletionResult } from '@/sync';
import { DailyRewardButton } from '@/ui/daily-reward-button';
import { DailyRewardScreen } from '@/ui/daily-reward-screen';
import { getActiveReward } from '@/game/reward-application';

// Built-in levels
import firstDerivativeData from '../levels/first-derivative.json';
import harmonicOscillatorData from '../levels/harmonic-oscillator.json';
import phaseShiftData from '../levels/phase-shift.json';
import intoTheComplexData from '../levels/into-the-complex.json';
import polarExpressData from '../levels/polar-express.json';
import asymptoteData from '../levels/asymptote.json';

// Level registry
const BUILT_IN_LEVELS: { data: LevelData; info: LevelInfo }[] = [
  { data: firstDerivativeData as unknown as LevelData,
    info: { id: 'first-derivative', name: 'First Derivative', difficulty: 'Easy', bpm: 110 } },
  { data: harmonicOscillatorData as unknown as LevelData,
    info: { id: 'harmonic-oscillator', name: 'Harmonic Oscillator', difficulty: 'Easy', bpm: 116 } },
  { data: phaseShiftData as unknown as LevelData,
    info: { id: 'phase-shift', name: 'Phase Shift', difficulty: 'Normal', bpm: 120 } },
  { data: intoTheComplexData as unknown as LevelData,
    info: { id: 'into-the-complex', name: 'Into the Complex', difficulty: 'Normal', bpm: 128 } },
  { data: polarExpressData as unknown as LevelData,
    info: { id: 'polar-express', name: 'Polar Express', difficulty: 'Normal', bpm: 132 } },
  { data: asymptoteData as unknown as LevelData,
    info: { id: 'asymptote', name: 'Asymptote', difficulty: 'Hard', bpm: 140 } },
];

// Level unlock progression
const LEVEL_UNLOCK_ORDER = [
  'first-derivative', 'harmonic-oscillator', 'phase-shift', 'into-the-complex', 'polar-express', 'asymptote',
];

function init(): void {
  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  const overlay = document.getElementById('overlay') as HTMLElement;
  const hudButtons = document.getElementById('hud-buttons') as HTMLElement;
  const pauseOverlay = document.getElementById('pause-overlay') as HTMLElement;

  // HUD buttons
  const btnPause = document.getElementById('btn-pause') as HTMLButtonElement;
  const btnExit = document.getElementById('btn-exit') as HTMLButtonElement;
  const btnTuning = document.getElementById('btn-tuning') as HTMLButtonElement;

  // Pause overlay buttons
  const btnResume = document.getElementById('btn-resume') as HTMLButtonElement;
  const btnPauseExit = document.getElementById('btn-pause-exit') as HTMLButtonElement;

  if (!canvas) throw new Error('Canvas element not found');

  // Persistent systems
  const storage = new StorageManager();
  const account = new Account(storage);
  const syncManager = new SyncManager(storage);
  const accountScreen = new AccountScreen(account);
  const browseScreen = new BrowseScreen(storage, syncManager);
  browseScreen.onBack = () => { showTitleScreen(); };

  // Show account screen while making sure the title-screen "tap/key to start"
  // listeners don't fire on events originating inside the account overlay.
  function openAccountScreen(): void {
    window.removeEventListener('keydown', onStartInput);
    overlay.removeEventListener('pointerdown', onStartInput);
    setMenuChromeVisible(false);
    accountScreen.show();
  }

  setOnAuthLost(() => {
    storage.clearSession();
    toast('Session expired — please sign in again', { variant: 'error', durationMs: 4000 });
    dailyRewardButton.refresh();
    openAccountScreen();
  });

  // Persistent account button — lives on document.body so it's visible on
  // title and menu screens (not during gameplay; hidden when HUD shows).
  const accountBtnWrapper = document.createElement('div');
  accountBtnWrapper.id = 'account-btn-wrapper';
  accountBtnWrapper.style.cssText = 'position:fixed;top:16px;right:16px;z-index:65;';
  accountBtnWrapper.innerHTML = '<button id="btn-account" style="background:rgba(10,10,26,0.8);color:#00ffff;border:1px solid #00ffff44;padding:8px 14px;font-family:\'Courier New\',monospace;font-size:0.8rem;border-radius:4px;cursor:pointer;min-height:40px;backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);">&#x1F464; SIGN IN</button>';
  document.body.appendChild(accountBtnWrapper);
  document.getElementById('btn-account')?.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    openAccountScreen();
  });
  accountScreen.onBack = () => { dailyRewardButton.refresh(); showTitleScreen(); };

  function updateAccountButton(): void {
    const btn = document.getElementById('btn-account');
    if (!btn) return;
    const sess = storage.getSession();
    btn.textContent = sess ? `\u{1F464} ${sess.displayName}` : `\u{1F464} SIGN IN`;
  }

  function setAccountButtonVisible(visible: boolean): void {
    accountBtnWrapper.style.display = visible ? '' : 'none';
  }

  // Daily reward button + screen
  const dailyRewardButton = new DailyRewardButton(storage);
  dailyRewardButton.mount();

  const dailyRewardAudio = new AudioEngine();
  const dailyRewardSfx = new SFX(dailyRewardAudio);
  const dailyRewardScreen = new DailyRewardScreen(storage, undefined, undefined, dailyRewardSfx);
  dailyRewardScreen.onClose = () => {
    dailyRewardButton.refresh();
    showTitleScreen();
  };
  dailyRewardScreen.onClaimed = () => {
    dailyRewardButton.refresh();
  };

  dailyRewardButton.onClick = () => {
    dailyRewardAudio.init();
    window.removeEventListener('keydown', onStartInput);
    overlay.removeEventListener('pointerdown', onStartInput);
    setMenuChromeVisible(false);
    dailyRewardScreen.show();
  };

  function setMenuChromeVisible(visible: boolean): void {
    setAccountButtonVisible(visible);
    dailyRewardButton.setVisible(visible);
    if (visible) dailyRewardButton.refresh();
  }

  function openBrowseScreen(): void {
    if (!storage.getSession()) {
      toast('Sign in to browse other kids\' levels', { variant: 'info', durationMs: 3000 });
      return;
    }
    window.removeEventListener('keydown', onStartInput);
    overlay.removeEventListener('pointerdown', onStartInput);
    browseScreen.show();
  }

  // Animated title-screen background (grid + scrolling terrain + motes).
  // Runs on every non-gameplay screen so the app always feels alive.
  const titleBackground = new TitleBackground(canvas);
  const menuMusic = new MenuMusic();

  const resultsScreen = new ResultsScreen();
  const levelSelect = new LevelSelectScreen();
  const settingsScreen = new SettingsScreen();

  levelSelect.setSync(syncManager, storage);

  let game: Game | null = null;
  const editorScreen = new EditorScreen({
    storage,
    syncManager,
    onExit: () => {
      editorScreen.hide();
      showTitleScreen();
    },
    resolveBuiltin: (id: string) => BUILT_IN_LEVELS.find((l) => l.info.id === id)?.data,
    onTestRequest: (data, startOffset, onFinish) => {
      hudButtons.style.display = 'flex';
      const g = new Game(canvas);
      g.loadLevelData(data);
      g.applyDailyReward(getActiveReward(storage), data.settings.baseColor);
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        const exitScroll = g.currentScrollOffset;
        g.stop();
        hudButtons.style.display = 'none';
        onFinish(exitScroll);
      };
      g.onExitRequested = finish;
      g.onLevelComplete = () => finish();
      g.onStateChange = (state) => {
        if (state === 'dead') {
          setTimeout(finish, 900);
        }
      };
      game = g;
      g.start({ startOffset });
    },
  });
  let currentLevelId = 'first-derivative';

  editorScreen.onSyncRequest = async (action) => {
    const level = editorScreen.currentLevel;
    if (!level) return;
    await performSyncAction(action, level, { storage, syncManager });
  };

  // ─── Screen Flow ─────────────────────────────────────────

  function showTitleScreen(): void {
    if (game) {
      game.stop();
      game = null;
    }
    resultsScreen.hide();
    levelSelect.hide();
    settingsScreen.hide();

    updateAccountButton();
    setMenuChromeVisible(true);
    titleBackground.start();
    menuMusic.start();

    overlay.style.transition = 'opacity 0.4s';
    overlay.style.opacity = '1';
    overlay.classList.add('active');
    hudButtons.style.display = 'none';
    pauseOverlay.style.display = 'none';

    // Hide combo HUD if present
    const comboHud = document.getElementById('combo-hud');
    if (comboHud) comboHud.style.opacity = '0';
    const triggerText = document.getElementById('trigger-text');
    if (triggerText) triggerText.style.opacity = '0';

    setTimeout(() => {
      window.addEventListener('keydown', onStartInput);
      overlay.addEventListener('pointerdown', onStartInput);
    }, 100);
  }

  function showLevelSelect(): void {
    window.removeEventListener('keydown', onStartInput);
    overlay.removeEventListener('pointerdown', onStartInput);
    overlay.classList.remove('active');
    overlay.style.opacity = '0';
    updateAccountButton();
    setMenuChromeVisible(true);
    titleBackground.start();
    menuMusic.start();
    levelSelect.show(
      BUILT_IN_LEVELS.map((l) => l.info),
      storage.getUserLevels(),
      storage,
    );
  }

  function startLevelData(level: LevelData): void {
    overlay.classList.remove('active');
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.5s';
    hudButtons.style.display = 'flex';
    pauseOverlay.style.display = 'none';
    setMenuChromeVisible(false);
    titleBackground.stop();
    menuMusic.stop();

    window.removeEventListener('keydown', onStartInput);
    overlay.removeEventListener('pointerdown', onStartInput);

    if (game) { game.stop(); game = null; }
    game = new Game(canvas);
    game.loadLevelData(level);
    game.applyDailyReward(getActiveReward(storage), level.settings.baseColor);

    game.onStateChange = (state) => {
      if (state === 'paused') {
        pauseOverlay.style.display = 'flex';
        btnPause.textContent = '▶';
        btnPause.title = 'Resume';
      } else if (state === 'playing') {
        pauseOverlay.style.display = 'none';
        btnPause.textContent = '❚❚';
        btnPause.title = 'Pause';
      }
    };

    game.onLevelComplete = async (breakdown, runData) => {
      hudButtons.style.display = 'none';
      setMenuChromeVisible(true);
      titleBackground.start();
      menuMusic.start();

      // Save to storage
      const prevBest = storage.getRecord(runData.levelId).bestScore;
      const isNewBest = breakdown.finalScore > prevBest;
      storage.updateRecord(
        runData.levelId,
        breakdown.finalScore,
        runData.timeMs,
        runData.starsCollected,
        runData.fragmentsCollected,
        runData.peakGrazeStreak,
        breakdown.eleganceRatio,
        true,
        1,
      );

      // Unlock next built-in level (only applies to built-in levels)
      const idx = LEVEL_UNLOCK_ORDER.indexOf(runData.levelId);
      if (idx >= 0 && idx < LEVEL_UNLOCK_ORDER.length - 1) {
        storage.unlock(LEVEL_UNLOCK_ORDER[idx + 1]);
      }

      // Show results — use level name from meta (built-ins also have correct name there)
      const levelName = BUILT_IN_LEVELS.find((l) => l.info.id === runData.levelId)?.info.name ?? level.meta.name;

      // NEW: Submit completion to server — only for server-stored levels, only on clean runs.
      let completionResult: CompletionResult | null = null;
      if (level.meta.remoteId && !runData.practiceUsedThisRun) {
        completionResult = await submitCompletion(
          level.meta.remoteId,
          level.meta.remoteVersion ?? 0,
          { syncManager },
        );
      }

      resultsScreen.show(breakdown, levelName, runData.timeMs, isNewBest, completionResult);
    };

    resultsScreen.onRetry = () => {
      startLevelData(level);
    };

    resultsScreen.onExit = () => {
      showTitleScreen();
    };

    game.start();
    (window as unknown as Record<string, unknown>).game = game;
  }

  function startLevel(levelId: string): void {
    currentLevelId = levelId;
    const builtIn = BUILT_IN_LEVELS.find((l) => l.info.id === levelId);
    const level = builtIn ? builtIn.data : storage.getUserLevel(levelId);
    if (!level) return;
    startLevelData(level);
  }

  function startDownloadedLevel(level: LevelData): void {
    startLevelData(level);
  }

  function playUserLevel(lvl: LevelData): void {
    startLevelData(lvl);
  }

  function onStartInput(e: Event): void {
    e.preventDefault();
    showLevelSelect();
  }

  // ─── Level Select Callbacks ──────────────────────────────

  levelSelect.onSelect = (levelId, kind) => {
    if (kind === 'builtin') startLevel(levelId);
    else {
      const lvl = storage.getUserLevel(levelId);
      if (lvl) playUserLevel(lvl);
    }
  };

  levelSelect.onBack = () => {
    showTitleScreen();
  };

  levelSelect.onUnlockAll = () => {
    for (const level of BUILT_IN_LEVELS) {
      storage.unlock(level.info.id);
    }
    // Re-show level select with everything unlocked
    showLevelSelect();
  };

  levelSelect.onEdit = (levelId) => {
    const lvl = storage.getUserLevel(levelId);
    if (!lvl) return;
    levelSelect.hide();
    setMenuChromeVisible(false);
    titleBackground.stop();
    menuMusic.stop();
    editorScreen.show();
    void editorScreen.openLevel(lvl);
  };

  levelSelect.onClone = (levelId, kind) => {
    const src = kind === 'builtin'
      ? BUILT_IN_LEVELS.find((l) => l.info.id === levelId)?.data
      : storage.getUserLevel(levelId);
    if (!src) return;
    const author = storage.settings.defaultAuthor ?? '';
    const clone = cloneLevel(src, kind, author);
    storage.upsertUserLevel(clone);
    showLevelSelect();
  };

  levelSelect.onDelete = async (levelId) => {
    const ok = await showConfirm('Delete level', 'This cannot be undone.');
    if (!ok) return;
    storage.removeUserLevel(levelId);
    showLevelSelect();
  };

  levelSelect.onNew = () => {
    const blank = newBlankLevel(storage.settings.defaultAuthor ?? 'Anonymous');
    storage.upsertUserLevel(blank);
    levelSelect.hide();
    setMenuChromeVisible(false);
    titleBackground.stop();
    menuMusic.stop();
    editorScreen.show();
    void editorScreen.openLevel(blank);
  };

  levelSelect.onImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return;
      const text = await file.text();
      const res = parseImport(text);
      if (res.kind !== 'ok') { await showAlert('Import failed.'); return; }
      const existing = storage.getUserLevels();
      const plan = planConflicts(res.levels, existing);
      for (const p of plan) p.resolution = p.conflict ? 'skip' : 'replace';
      const updated = applyConflictPlan(plan, existing);
      for (const l of updated) storage.upsertUserLevel(l);
      showLevelSelect();
    });
    input.click();
  };

  levelSelect.onSyncAction = async (action, levelId) => {
    const level = storage.getUserLevel(levelId);
    if (!level) return;
    await performSyncAction(action, level, { storage, syncManager });
    // Re-open Level Select to refresh chips
    levelSelect.show(
      BUILT_IN_LEVELS.map((l) => l.info),
      storage.getUserLevels(),
      storage,
    );
  };

  levelSelect.onPullRemoteOnly = async (remoteId) => {
    await pullRemoteOnlyLevel(remoteId, { storage, syncManager });
    // Re-render so the pulled level moves from "remote-only" to "synced"
    levelSelect.show(
      BUILT_IN_LEVELS.map((l) => l.info),
      storage.getUserLevels(),
      storage,
    );
  };

  levelSelect.onBrowse = () => openBrowseScreen();

  levelSelect.onDownloadedAction = async (action, remoteId) => {
    const downloaded = storage.getDownloadedLevels().find((l) => l.meta.remoteId === remoteId);
    if (!downloaded) return;
    if (action === 'play') {
      startDownloadedLevel(downloaded);
      return;
    }
    if (action === 'remix') {
      await remixLevelAction(remoteId, { storage, syncManager });
    } else if (action === 'unlink') {
      unlinkDownloadedLevel(remoteId, { storage });
    } else if (action === 'pull') {
      await updateDownloadedLevel(downloaded, { storage, syncManager });
    }
    // Re-render the Downloaded tab to pick up changes
    levelSelect.show(
      BUILT_IN_LEVELS.map((l) => l.info),
      storage.getUserLevels(),
      storage,
    );
  };

  // ─── Settings Callbacks ──────────────────────────────────

  settingsScreen.onChange = (settings) => {
    storage.updateSettings(settings);
  };

  settingsScreen.onClose = () => {
    showTitleScreen();
  };

  // ─── Button Wiring ──────────────────────────────────────

  const stopProp = (e: Event) => e.stopPropagation();

  btnPause.addEventListener('pointerdown', (e) => {
    stopProp(e);
    if (!game) return;
    if (game.isPaused) {
      game.resume();
    } else {
      game.pause();
    }
  });

  btnExit.addEventListener('pointerdown', (e) => {
    stopProp(e);
    if (game && game.onExitRequested) {
      game.onExitRequested();
    } else {
      showTitleScreen();
    }
  });

  btnTuning.addEventListener('pointerdown', (e) => {
    stopProp(e);
    game?.toggleTuning();
  });

  btnResume.addEventListener('pointerdown', (e) => {
    stopProp(e);
    game?.resume();
  });

  btnPauseExit.addEventListener('pointerdown', (e) => {
    stopProp(e);
    showTitleScreen();
  });

  // Initial state: title screen
  updateAccountButton();
  titleBackground.start();
  menuMusic.start();
  window.addEventListener('keydown', onStartInput);
  overlay.addEventListener('pointerdown', onStartInput);

  // Register service worker (production only) with update detection
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    navigator.serviceWorker.register('/service-worker.js').then((reg) => {
      setInterval(() => reg.update(), 60_000);

      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'activated' && navigator.serviceWorker.controller) {
            showUpdateBanner();
          }
        });
      });
    }).catch((err) => {
      console.warn('SW registration failed:', err);
    });
  }

  console.log(
    '%c🔺 TriggernometryDash %cv0.2.0',
    'color: #00ffff; font-size: 16px; font-weight: bold;',
    'color: #ff00ff; font-size: 14px;',
  );
}

function newBlankLevel(author: string): LevelData {
  const now = new Date().toISOString();
  return {
    version: 1,
    meta: {
      id: crypto.randomUUID(),
      name: 'Untitled', description: '', author,
      difficulty: 'easy', version: 1, parTime: 30000,
      optimalInputs: 10, tags: [],
      createdAt: now, updatedAt: now,
    },
    settings: {
      initialMode: 'real', initialSpeed: 1, initialPlayerMode: 'triangle',
      baseColor: '#00ffff', accentColor: '#ff00ff', backgroundColor: '#0a0a1a',
      gridIntensity: 0.8,
      music: { bpm: 120, key: 'Am', style: 'synthwave', layers: ['bass', 'drums'] },
      fog: { near: 50, far: 200, color: '#0a0a1a' },
    },
    segments: [{
      id: 'seg-01', mode: 'real', length: 2000, speed: 1,
      terrain: { type: 'function', floor: { function: 'sin', amplitude: 30, frequency: 0.01, offset: 100, phase: 0 }, ceiling: null },
      obstacles: [], decorations: [],
    }],
    triggers: [], collectibles: [],
  };
}

function showUpdateBanner(): void {
  const banner = document.createElement('div');
  banner.style.cssText = `
    position: fixed; bottom: 16px; left: 50%; transform: translateX(-50%);
    background: rgba(10, 10, 26, 0.92); border: 1px solid #00ffff88;
    border-radius: 6px; padding: 10px 20px; z-index: 2000;
    font-family: 'Courier New', monospace; font-size: 14px;
    color: #00ffff; cursor: pointer; backdrop-filter: blur(4px);
    display: flex; align-items: center; gap: 12px;
    animation: pulse 1.5s ease-in-out infinite;
  `;
  banner.innerHTML = `New version available <span style="color:#ff00ff;font-weight:bold">TAP TO UPDATE</span>`;
  banner.addEventListener('pointerdown', () => window.location.reload());
  document.body.appendChild(banner);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
